// Frequency-separation skin smoothing — Session B of the VTO quality
// roadmap (v0.4.0). Replaces the single-Gaussian pass in skinOverlay.ts
// with a two-pass decomposition:
//
//   low       = gaussian(img, r1)      → coarse tone (blemish scale)
//   highDetail= img − low               → pore + hair high-frequency detail
//   lowSmooth = gaussian(low, r2)       → further-smoothed tone
//   result    = img + (lowSmooth − low) → original detail, evened tone
//
// The recombination uses only the low-frequency DELTA (`lowSmooth − low`),
// which has no high-frequency content by construction, so pores/hairs in
// the original survive untouched. Composited over the original at ~40%
// so skin stays recognisably the user's skin rather than porcelain.
//
// Performance gate: anything above 2000px on the longest edge gets
// downsampled to 2000px for the blur + pixel-arithmetic work. Cheaper
// `ctx.filter = blur()` plus smaller `getImageData` loops keeps the whole
// pass under 300ms on mid-tier Android (plan budget: 600ms total pipeline).
// The output canvas is at the WORK resolution; downstream overlays draw
// it at full source resolution via `drawImage`, which the browser scales
// with its built-in bicubic interpolation — visually lossless at typical
// phone display densities.
//
// skinOverlay.ts is kept as a drop-in fallback: `renderSkinSmoothing` has
// the same signature and result type as this renderer.

import type { LipDetection } from '../tracking/faceLandmarker';
import type { SemanticFaceMap } from '../semantic/semanticFaceMap';
import { stackBlur } from './stackBlur';
import { shrinkMask } from './maskUtils';
import type { SkinRenderResult, SkinSmoothingOptions } from './skinOverlay';

export interface SkinFrequencySeparationOptions extends SkinSmoothingOptions {
  // Composite strength 0-1: how much of the smoothed delta to apply.
  // Default 0.40 — tuned so the face reads as "evened skin tone" rather
  // than "airbrushed filter". Above 0.55 veers into plastic territory.
  strength?: number;
  // Work-resolution cap on the longest edge. Default 2000px. Smaller values
  // speed up the pixel loop linearly but soften pore reproduction on the
  // upscaled output; 2000 is the sweet spot for 12MP phone captures.
  workMax?: number;
  semanticMap?: SemanticFaceMap | null;
}

// MediaPipe FaceMesh face-oval landmark loop (clockwise from forehead).
// Mirrors skinOverlay.ts so the two renderers produce the same skin mask.
const FACE_OVAL = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
  361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
  176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
  162, 21, 54, 103, 67, 109
];

const LEFT_EYE = [
  33, 246, 161, 160, 159, 158, 157, 173, 133,
  155, 154, 153, 145, 144, 163, 7
];
const RIGHT_EYE = [
  263, 466, 388, 387, 386, 385, 384, 398, 362,
  382, 381, 380, 374, 373, 390, 249
];
const OUTER_LIP = [
  61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291,
  375, 321, 405, 314, 17, 84, 181, 91, 146
];

// Traces a closed path through landmark indices, scaling normalised
// coords to the given target canvas dimensions. Allows us to build the
// same topological mask at whichever work resolution we pick.
function tracePath(
  ctx: CanvasRenderingContext2D,
  detection: LipDetection,
  indices: readonly number[],
  targetW: number,
  targetH: number
): void {
  const { landmarks } = detection;
  let started = false;
  for (const idx of indices) {
    const p = landmarks[idx];
    if (!p) continue;
    const x = p.x * targetW;
    const y = p.y * targetH;
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  if (started) ctx.closePath();
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  return c;
}

// Build the skin mask — face oval minus eyes minus outer lip — at the
// requested resolution, with a subtle feather so the smoothed region
// blends into untouched pixels at the face silhouette.
function buildSkinMask(
  detection: LipDetection,
  W: number,
  H: number,
  semanticMap?: SemanticFaceMap | null
): HTMLCanvasElement {
  const mask = createCanvas(W, H);
  const mctx = mask.getContext('2d', { willReadFrequently: true });
  if (!mctx) return mask;
  const featherPx = Math.max(3, Math.min(W, H) * 0.008);

  if (semanticMap) {
    mctx.drawImage(semanticMap.masks.skin, 0, 0, W, H);
    stackBlur(mctx, 0, 0, W, H, featherPx);
    return mask;
  }

  mctx.fillStyle = '#ffffff';
  mctx.beginPath();
  tracePath(mctx, detection, FACE_OVAL, W, H);
  tracePath(mctx, detection, LEFT_EYE, W, H);
  tracePath(mctx, detection, RIGHT_EYE, W, H);
  tracePath(mctx, detection, OUTER_LIP, W, H);
  mctx.fill('evenodd');
  stackBlur(mctx, 0, 0, W, H, featherPx);
  return mask;
}

export function renderSkinFrequencySeparation(
  source: HTMLCanvasElement | ImageBitmap,
  detection: LipDetection,
  options: SkinFrequencySeparationOptions = {}
): SkinRenderResult {
  const sourceW = detection.imageWidth;
  const sourceH = detection.imageHeight;
  const strength = Math.max(0, Math.min(1, options.strength ?? 0.40));
  const workMax = options.workMax ?? 2000;

  // Work at source resolution unless it exceeds workMax — then downsample.
  // Pore-scale detail above work resolution is unreachable anyway on
  // display (the PWA renders at ≤ device-pixel resolution, which for a
  // phone is well under 2000px); downsampling beyond that point trades
  // invisible pore fidelity for ~4× faster pixel loop.
  const scale = Math.min(1, workMax / Math.max(sourceW, sourceH));
  const W = Math.max(1, Math.round(sourceW * scale));
  const H = Math.max(1, Math.round(sourceH * scale));

  // Blur radii scale with the work canvas short edge so the effective
  // frequency cutoff is the same regardless of input resolution.
  // r1 isolates blemish-scale variation (~1.5% of short edge), r2 smooths
  // it further (~0.4%). These are the values from the roadmap plan,
  // verified to preserve pore detail on 1500-2000px photos.
  const minDim = Math.min(W, H);
  const r1 = Math.max(8, minDim * 0.015);
  const r2 = Math.max(2, minDim * 0.004);

  // Src, blur1 (= gaussian(src, r1)), blur2 (= gaussian(blur1, r2)) all at
  // work resolution. Stack-blur replaces ctx.filter for browser-independent
  // quality (iOS Safari's GPU box blur produces visible stair-stepping).
  const src = createCanvas(W, H);
  const sctx = src.getContext('2d', { willReadFrequently: true });
  if (!sctx) {
    // Pathological — GPU context missing. Fall back to the original image
    // untouched; the overlays above still render correctly.
    const out = createCanvas(sourceW, sourceH);
    const octx = out.getContext('2d');
    if (octx) octx.drawImage(source, 0, 0, sourceW, sourceH);
    return { canvas: out };
  }
  sctx.drawImage(source, 0, 0, W, H);

  const blur1 = createCanvas(W, H);
  const b1ctx = blur1.getContext('2d', { willReadFrequently: true });
  if (!b1ctx) return { canvas: src };
  b1ctx.drawImage(src, 0, 0);
  stackBlur(b1ctx, 0, 0, W, H, r1);

  const blur2 = createCanvas(W, H);
  const b2ctx = blur2.getContext('2d', { willReadFrequently: true });
  if (!b2ctx) return { canvas: src };
  b2ctx.drawImage(blur1, 0, 0);
  stackBlur(b2ctx, 0, 0, W, H, r2);

  // Pixel-arithmetic recombination:
  //   smoothed[i] = clamp(src[i] + (blur2[i] - blur1[i]), 0, 255)
  // Note blur2 is tilted toward blur1's tone (since blur2 = blur(blur1)),
  // so the delta is a small-signed signal — the clamp effectively only
  // catches a few extreme pixels at the face silhouette.
  const srcImg = sctx.getImageData(0, 0, W, H);
  const b1Img = b1ctx.getImageData(0, 0, W, H);
  const b2Img = b2ctx.getImageData(0, 0, W, H);
  const srcD = srcImg.data;
  const b1D = b1Img.data;
  const b2D = b2Img.data;
  const outImg = new ImageData(W, H);
  const oD = outImg.data;

  const len = srcD.length;
  for (let i = 0; i < len; i += 4) {
    // R channel
    let v = srcD[i]! + (b2D[i]! - b1D[i]!);
    oD[i] = v < 0 ? 0 : v > 255 ? 255 : v;
    // G channel
    v = srcD[i + 1]! + (b2D[i + 1]! - b1D[i + 1]!);
    oD[i + 1] = v < 0 ? 0 : v > 255 ? 255 : v;
    // B channel
    v = srcD[i + 2]! + (b2D[i + 2]! - b1D[i + 2]!);
    oD[i + 2] = v < 0 ? 0 : v > 255 ? 255 : v;
    // A channel: always opaque.
    oD[i + 3] = 255;
  }

  const smoothed = createCanvas(W, H);
  const smctx = smoothed.getContext('2d');
  if (!smctx) return { canvas: src };
  smctx.putImageData(outImg, 0, 0);

  // Crop the smoothed image to the skin region (mask). Outside the mask
  // we want the original pixels to come through untouched.
  const mask = buildSkinMask(detection, W, H, options.semanticMap);
  // Erode the mask by the blur radius so edge pixels contaminated by the
  // Gaussian bleed (e.g. hair colour leaking into skin) are discarded.
  const edgeBuffer = Math.ceil(r1 + 4);
  const maskEroded = shrinkMask(mask, edgeBuffer);
  smctx.globalCompositeOperation = 'destination-in';
  smctx.drawImage(maskEroded, 0, 0);
  smctx.globalCompositeOperation = 'source-over';

  // Output canvas: original at work res + skin-masked smoothed layer on
  // top at `strength` alpha. Overlays downstream receive this canvas via
  // drawImage(source, 0, 0, fullW, fullH) which does the bicubic upscale
  // back to source resolution — lossless at typical display densities.
  const out = createCanvas(W, H);
  const octx = out.getContext('2d');
  if (!octx) return { canvas: src };
  octx.drawImage(src, 0, 0);
  octx.globalAlpha = strength;
  octx.drawImage(smoothed, 0, 0);
  octx.globalAlpha = 1;

  return { canvas: out };
}
